import { useState, useEffect, useCallback, useMemo } from 'react';
import { useRoleApi } from '../../../hooks/useRoleApi';
import { useRoleCategoryApi } from '../../../hooks/useRoleCategoryApi';

export const useAllRolesViewModel = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const {
    getRolesCategory,
    deleteRoleCategory,
    createRoleCategory,
    updateRoleCategory: apiUpdateRoleCategory,
    cacheOperations: { writeRolesCategories, readRolesCategories }
  } = useRoleCategoryApi();

  const { cacheOperations: { readRoles } } = useRoleApi()
  const {
    getRoles,
    deleteRole,
    createRole,
    updateRole: apiUpdateRole
  } = useRoleApi();


  const fetchRoles = useCallback(async () => {
    if (!loading) setLoading(true);
    try {
      const result = await getRoles();
      await getRolesCategory()
      if (result.success) {
        setRoles(result.data);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getRoles]);

  // Only fetch on mount
  useEffect(() => {
    fetchRoles();
  }, []); // Remove fetchRoles from dependencies

  const getRoleName = useMemo(
    () => (roleId) => {
      const role = readRoles();

      const roleName = role.find(
        (rol) => rol.role_id.toString() === roleId?.toString()
      );

      return roleName?.role_name || "Unknown";
    },
    [readRoles]
  );

  const handleDelete = useCallback(async (role) => {
    try {
      const result = await deleteRole(parseInt(role.role_id));
      if (result.success) {
        await fetchRoles();
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    }
  }, [deleteRole, fetchRoles]);

  const handleCreateRole = useCallback(async (formData) => {
    try {
      const result = await createRole(formData);
      if (result.success) {
        await fetchRoles();
        return { success: true };
      }
    } catch (error) {
      // Pass through the error message
      throw new Error(error.message);
    }
  }, [createRole, fetchRoles]);

  const updateRole = useCallback(async (roleId, formData) => {
    try {
      const result = await apiUpdateRole(roleId, formData);
      if (result.success) {
        await fetchRoles();
        return { success: true };
      }
    } catch (error) {
      // Pass through the error message
      throw new Error(error.message);
    }
  }, [apiUpdateRole, fetchRoles]);

  const getRoleCategoryName = useMemo(
    () => (id) => {
      const rolesCategories = readRolesCategories();
      const rolesCategory = rolesCategories.find(
        (roleCat) => roleCat.role_category_id.toString() === id?.toString()
      );
      return rolesCategory?.role_cat_name || "Unknown";
    },
    [readRolesCategories]
  );

  return {
    roles,
    loading: loading,
    error,
    refetch: fetchRoles,
    handleDelete,
    createRole: handleCreateRole,
    updateRole,
    getRoleName,
    roleCategory: readRolesCategories(),
    getRoleCategoryName
  };
}; 